import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/auth';
import Button from '../../components/ui/Button';

const ConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        // Vérifier si nous avons les paramètres nécessaires
        const token = searchParams.get('token');
        const type = searchParams.get('type');

        if (!token || type !== 'signup') {
          setStatus('error');
          setMessage('Lien de confirmation invalide ou expiré.');
          return;
        }

        // Vérifier la session actuelle
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Erreur lors de la vérification de la session:', error);
          setStatus('error');
          setMessage('Erreur lors de la confirmation de votre email.');
          return;
        }

        if (session?.user?.email_confirmed_at) {
          setStatus('success');
          setMessage('Votre adresse email a bien été confirmée.');
        } else {
          setStatus('error');
          setMessage('Votre email n\'a pas pu être confirmé. Veuillez réessayer.');
        }
      } catch (error) {
        console.error('Erreur lors de la confirmation:', error);
        setStatus('error');
        setMessage('Une erreur inattendue s\'est produite.');
      }
    };

    confirmEmail();
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
          <div className="text-center">
            {status === 'loading' && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-12 w-12 text-pink-600 animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Confirmation en cours...
                </h2>
                <p className="text-gray-600">
                  Nous vérifions votre adresse email.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Email confirmé !
                </h2>
                <p className="text-gray-600 mb-6">
                  {message}
                </p>
                <Button onClick={handleContinue} fullWidth>
                  Continuer vers le dashboard
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertCircle className="h-12 w-12 text-red-600" />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Erreur de confirmation
                </h2>
                <p className="text-gray-600 mb-6">
                  {message}
                </p>
                <div className="space-y-3">
                  <Button onClick={() => navigate('/login')} fullWidth>
                    Retour à la connexion
                  </Button>
                  <Button variant="outline" onClick={() => window.location.reload()} fullWidth>
                    Réessayer
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmPage;